import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import Dialog from 'lib/components/DialogV2';
import SMSCode from 'lib/components/SMSCodeV2';
import TipsModalV2 from 'lib/components/Modal/TipsModalV2';
import { CAPTCHA_LENGTH, DEFAULT_AGREEMENT_CONFIG, OPERATOR_TYPE, SMS_MODAL_STYLE } from 'lib/constants/operator';
import { fetchDeliverSMSCode, fetchSubmitOrder, querySubscribeFailReason } from 'lib/apis';
import { currentUrlParams } from 'lib/utils/url';
import toast from 'lib/utils/toast';
import { ERROR_MSG, ORDER_SMS_CODE_ERROR_MSG } from 'lib/constants/error';
import { showModal, closeModal } from 'lib/components/Modal';
import { useCountDown } from 'lib/hooks/useCountDown';
import storage from 'lib/utils/storage';
import { generateYZFOrderIdKey, STORAGE } from 'lib/utils/storage/constants';
import { report } from 'lib/utils/dataReport';
import { ATTA_EVENT_RESULT, ATTA_REPORT } from 'lib/constants/atta';
import channelService from 'lib/services/channelService';
import monitor, { TRACE_ENUM } from 'lib/utils/monitor';
import { ORDER_STATE } from 'lib/constants/order';
import { getLoginType } from 'lib/utils/util';
import { ENV_TYPE } from 'lib/constants/login';
import { useGlobalStore } from 'lib/store';
import OrderFail from '../OrderFail';
import OrderCompleteV2 from '../OrderCompleteV2';
import { showDetainModal } from '../RetainModal/DetainModal';
import CountDown from './CountDown';
import OrderGoods from './OrderGoods';
import PackageInfo from './PackageInfo';
import AgreementCheckbox, { AgreementCheckboxRef, AgreementCheckSource } from './AgreementCheckbox';
import AgreementText from './AgreementText';
import { buildAgreementReportParams, reportAgreementFallback } from './agreementReport';
import './index.scss';

function spacePhoneNumber(number) {
  return number.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1 $2 $3');
}
// 需要用toast展示的错误码
const toastCode = [20503, 20502, 20501, 20901, 20504];

const STYLE_CLASS_MAP: Record<string, string> = {
  [SMS_MODAL_STYLE.STYLE_1]: 'captcha-package__modal-style1',
  [SMS_MODAL_STYLE.STYLE_2]: 'captcha-package__modal-style2',
  [SMS_MODAL_STYLE.STYLE_3]: 'captcha-package__modal-style3',
};

const SMSCaptchaModal = ({
  operatorType,
  mobile,
  channelCode,
  packageName,
  packageId,
  priceDesc,
  orderTips,
  isPromotional = 0,
}) => {
  const [visible, setVisible] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const isFetching = useRef(false);
  const orderIdRef = useRef(storage.getStorage(STORAGE.SMSCODE_ORDER_ID));
  const isAutoSend = useRef(false);
  const hasShownRetainModal = useRef(false);
  const {
    smsModalMobilePromotionImg,
    smsModalDefaultButtonImg,
    isShowRetainModal,
    smsModalStyleMap,
    smsModalAgreement,
  } = useGlobalStore((state) => ({
    smsModalMobilePromotionImg: state.smsModalMobilePromotionImg,
    smsModalDefaultButtonImg: state.smsModalDefaultButtonImg,
    isShowRetainModal: state.isShowRetainModal,
    smsModalStyleMap: state.smsModalStyleMap,
    smsModalAgreement: state.smsModalAgreement,
  }));
  const agreementRef = useRef<AgreementCheckboxRef | null>(null);
  /**
   * 是否通过二次确认弹窗完成的本次订购同意。
   * 需求 BR-59：在前端订单提交埋点（复用既有事件 verify_submit_req）中追加 viaReconfirm=true 标记。
   * 仅在二次确认"同意并继续"路径置 true；下一次弹窗打开后不重置（弹窗销毁时整个组件重建，自动归零）。
   */
  const viaReconfirmRef = useRef<boolean>(false);

  const captchaLength = CAPTCHA_LENGTH.NORMAL;

  const resolvedStyleType = useMemo(() => {
    switch (operatorType) {
      case OPERATOR_TYPE.TELCOM: {
        const hasStyleMap = smsModalStyleMap && Object.keys(smsModalStyleMap).length > 0;
        if (!hasStyleMap) return SMS_MODAL_STYLE.STYLE_2;
        return smsModalStyleMap[packageId] || SMS_MODAL_STYLE.STYLE_2;
      }
      case OPERATOR_TYPE.CMCC:
        return SMS_MODAL_STYLE.STYLE_1;
      default:
        return null;
    }
  }, [operatorType, smsModalStyleMap, packageId]);

  const titleType: 'order' | 'benefit' = resolvedStyleType === SMS_MODAL_STYLE.STYLE_1 ? 'order' : 'benefit';
  const showOrderGoods = resolvedStyleType !== SMS_MODAL_STYLE.STYLE_3;
  const showPackageInfo = resolvedStyleType === SMS_MODAL_STYLE.STYLE_3;
  const showConfirmBtn = resolvedStyleType !== null;

  // 仅 dxyzf STYLE_3 弹窗 + 页面拖入 SMSAgreementConfig 组件并启用时，才启用协议合规流程
  const agreementConfig = useMemo(() => {
    if (resolvedStyleType !== SMS_MODAL_STYLE.STYLE_3) return null;
    return smsModalAgreement || null;
  }, [resolvedStyleType, smsModalAgreement]);
  const showAgreement = Boolean(agreementConfig);

  /**
   * EVT-24 协议配置降级埋点（modalTypeMismatch）：
   * 配置了 smsModalAgreement 但弹窗类型不是 dxyzf 时，前端忽略协议配置，按原弹窗类型渲染，并上报降级埋点。
   * 仅在弹窗实际可见后上报一次，避免组件首次挂载（visible=false）即重复上报。
   */
  useEffect(() => {
    if (!visible) return;
    if (smsModalAgreement && resolvedStyleType !== SMS_MODAL_STYLE.STYLE_3) {
      reportAgreementFallback('modalTypeMismatch', {
        packageId,
        agreementVersion: smsModalAgreement.agreementVersion,
        captchaModalType: resolvedStyleType || undefined,
      });
    }
  }, [visible, smsModalAgreement, resolvedStyleType, packageId]);

  const packageNameForPrice = useMemo(() => {
    // 1分钱：腾讯会员轻享版（促销版）——100000099213
    // 15元：腾讯会员轻享版——100000099199
    const pIdList = ['100000099199', '100000099213'];
    if (pIdList.includes(packageId))
      return packageName.replace(channelService.channelName || '', '').replace('专属的', '');
    if (resolvedStyleType === SMS_MODAL_STYLE.STYLE_3) return packageName.replace(channelService.channelName || '', '');
    return packageName;
  }, [packageId, packageName, resolvedStyleType]);

  const { countdown, startCountDown } = useCountDown(`captcha${mobile}`);

  useEffect(() => {
    if (countdown === null) return;
    // 请求验证码
    if (countdown > 0) {
      setVisible(true);
      isAutoSend.current = true;
      return;
    }
    !isAutoSend.current && deliverSMSCode({ isNotAuto: false });
    // deliverSMSCode 本身依赖 countdown，加入 deps 会导致循环调用；此处仅在倒计时归零时触发一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  useEffect(() => {
    report(
      ATTA_REPORT.VERIFY.VIEW,
      showPackageInfo ? { extraStatisticParams: { smsModalStyle: resolvedStyleType } } : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 仅首次挂载时上报一次，无需随 resolvedStyleType/showPackageInfo 变化重复上报

  const showSubmitingTips = () => {
    report(
      ATTA_REPORT.YZF.ORDER_SUBMITTED_POP_VIEW,
      showPackageInfo ? { extraStatisticParams: { smsModalStyle: resolvedStyleType } } : undefined,
    );
    TipsModalV2.show({
      content: '订购申请已提交，请30分钟后查看结果、领取权益',
      confirmText: '确定',
    });
  };

  const handleYZFOrderResult = async ({ packageName, priceDesc }) => {
    const loginType = getLoginType();
    // 端外无登录态都提示订购申请中
    if (loginType === ENV_TYPE.GUEST) {
      showSubmitingTips();
      return;
    }
    const res = await querySubscribeFailReason(orderIdRef.current);
    if (res?.code !== 0) {
      showSubmitingTips();
      return;
    }
    const orderState = res?.data?.orderState;
    switch (orderState) {
      case ORDER_STATE.CANCELING:
      case ORDER_STATE.CANCEL_SUCCESS:
      case ORDER_STATE.ORDER_SUCCESS:
        showModal(<OrderCompleteV2 packageName={packageName} priceDesc={priceDesc} />);
        break;
      default:
        showSubmitingTips();
    }
  };

  // 下发验证码
  const deliverSMSCode = async (options = {}) => {
    const { isNotAuto = false } = options;
    if (countdown) {
      setVisible(true);
      return;
    }
    toast.loading();
    const res = await fetchDeliverSMSCode({
      channelCode,
      packageId,
      mobile,
      shareToken: currentUrlParams.getCurrentParams('shareToken'),
      subStatTag: currentUrlParams.getCurrentParams('SubStatTag') || channelService.channelTag,
      promoterOrderId: currentUrlParams.getCurrentParams('promoterOrderId'),
    });

    const errMsg = res?.code !== 0 ? ERROR_MSG[res?.code] || '系统繁忙，请稍后再试' : '';
    if (errMsg && !visible) {
      const smsCodeErrMsg = ORDER_SMS_CODE_ERROR_MSG[res?.code];
      toast.close();
      TipsModalV2.show({
        content: smsCodeErrMsg || errMsg,
      });
      return;
    }
    toast.info(res?.code === 0 ? '验证码已下发' : '系统繁忙，请稍后再试');
    const expireTime = startCountDown(60);
    setVisible(true);
    report(isNotAuto ? ATTA_REPORT.VERIFY.CODEGET_CLICK : ATTA_REPORT.VERIFY.CODEGET_AUTOREQ, {
      eventResult: res?.code === 0 ? ATTA_EVENT_RESULT.SUCCESS : res?.code,
    });
    const { order_id: orderId } = res?.data || {};
    storage.setStorage(STORAGE.SMSCODE_ORDER_ID, orderId, expireTime);
    res?.code === 0 && (orderIdRef.current = orderId);
  };

  // 完成输入
  const completeInputCode = (smsCode) => {
    if (showConfirmBtn) {
      setCaptchaCode(smsCode);
      return;
    }
    ensureAgreementBeforeSubmit(() => {
      submitSMSCode(smsCode);
    });
  };

  // 提交验证码
  const submitSMSCode = async (smsCode) => {
    if (!smsCode) return;
    if (isFetching.current) return;
    isFetching.current = true;
    toast.loading();
    const res = await fetchSubmitOrder({
      orderId: orderIdRef.current,
      smsCode,
      mobile,
    });
    toast.close();
    isFetching.current = false;
    // BR-59：复用既有提交事件，附带 viaReconfirm 标记，便于按"二次确认转化"维度拆解订购漏斗
    const submitExtra = showAgreement
      ? buildAgreementReportParams(
          { packageId, agreementVersion: agreementConfig?.agreementVersion },
          { viaReconfirm: viaReconfirmRef.current ? 1 : 0 },
        )
      : undefined;
    report(ATTA_REPORT.VERIFY.SUBMIT, {
      eventResult: res?.code === 0 ? ATTA_EVENT_RESULT.SUCCESS : res?.code,
      ...(submitExtra ? { extraStatisticParams: submitExtra } : {}),
    });
    if (res?.code !== 0) {
      if (res?.code && toastCode.indexOf(res?.code) !== -1) {
        toast.info(ERROR_MSG[res?.code]);
        return;
      }
      showModal(<OrderFail code={res?.code} />);
      return;
    }

    monitor.reportLog({
      actionName: TRACE_ENUM.SUBMIT,
    });
    monitor.reportEvent(TRACE_ENUM.SUBMIT);

    // 缓存订单id用于翼支付场景查询结果, 且订购状态3秒后查询才有结果，因此先弹订购提交中
    if (operatorType === OPERATOR_TYPE.TELCOM) {
      storage.setStorage(generateYZFOrderIdKey(mobile), {
        orderId: orderIdRef.current,
        channelCode,
      });
      handleYZFOrderResult({ packageName, priceDesc });
      return;
    }

    // 弹出领取弹窗
    showModal(<OrderCompleteV2 packageName={packageName} priceDesc={priceDesc} />);
  };

  // 翼支付样式3协议合规：未勾选协议时弹二次确认；点击「同意并继续」自动勾选并继续提交
  const ensureAgreementBeforeSubmit = (onPass: () => void) => {
    if (!showAgreement || !agreementConfig) {
      onPass();
      return;
    }
    const isAgreed = agreementRef.current?.isChecked() ?? false;
    const reportContext = { packageId, agreementVersion: agreementConfig.agreementVersion };
    if (isAgreed) {
      // 已勾选直接放行，viaReconfirm 标记由 verify_submit_req 上报（BR-59）
      onPass();
      return;
    }
    if (!agreementConfig.enableReconfirm) {
      toast.info('请阅读并勾选同意协议后再继续订购');
      return;
    }
    // EVT-27 二次确认弹窗曝光
    report(ATTA_REPORT.VERIFY.AGREEMENT_RECONFIRM_VIEW, {
      extraStatisticParams: buildAgreementReportParams(reportContext),
    });
    TipsModalV2.showIndependent({
      title: agreementConfig.reconfirmTitle ?? DEFAULT_AGREEMENT_CONFIG.reconfirmTitle,
      // 富文本内容直接传 React 节点；不要叠 protocol-dialog__text，避免 height:374 撑高弹窗导致背景拉伸
      content: <AgreementText config={agreementConfig} packageId={packageId} />,
      confirmText: agreementConfig.reconfirmConfirmText ?? DEFAULT_AGREEMENT_CONFIG.reconfirmConfirmText,
      showClose: true,
      confirmCallback: () => {
        // EVT-28 二次确认弹窗"同意并继续"
        report(ATTA_REPORT.VERIFY.AGREEMENT_RECONFIRM_AGREE, {
          extraStatisticParams: buildAgreementReportParams(reportContext),
        });
        if (agreementRef.current) {
          agreementRef.current.setChecked(true, AgreementCheckSource.Reconfirm);
        }
        viaReconfirmRef.current = true;
        onPass();
      },
      onClose: () => {
        // EVT-29 二次确认弹窗"取消" — 用户点击关闭按钮 / ESC 时触发
        report(ATTA_REPORT.VERIFY.AGREEMENT_RECONFIRM_CANCEL, {
          extraStatisticParams: buildAgreementReportParams(reportContext),
        });
      },
    });
  };

  const submitConfirmCode = () => {
    report(ATTA_REPORT.VERIFY.CONFIRM_CLICK);
    ensureAgreementBeforeSubmit(() => {
      submitSMSCode(captchaCode);
    });
  };

  const handleCloseCallback = () => {
    report(ATTA_REPORT.VERIFY.CANCEL_CLICK);
    if (!isShowRetainModal) {
      return;
    }
    if (hasShownRetainModal.current) {
      closeModal();
      return;
    }
    showDetainModal({
      packageId,
      closeCallback: () => {
        closeModal();
      },
    });
    hasShownRetainModal.current = true;
  };

  if (!visible) return null;
  return (
    <Dialog
      keyboard={{
        onEnter: showConfirmBtn ? submitConfirmCode : undefined,
        allowEnterInInput: showConfirmBtn,
      }}
      customClass={cx(
        'captcha-package__modal',
        (resolvedStyleType && STYLE_CLASS_MAP[resolvedStyleType]) || 'captcha-package__modal-default',
      )}
      title={
        titleType === 'order' ? (
          <span className="captcha-package__title">您即将订购{packageName}</span>
        ) : (
          <span className="captcha-package__title">输入验证码 获取限时福利</span>
        )
      }
      closeCallback={handleCloseCallback}
      closeModalWhenClose={!isShowRetainModal}
    >
      <div className="captcha">
        {showOrderGoods && <OrderGoods packageId={packageId} />}
        {showPackageInfo && <PackageInfo />}
        <div className="captcha-package">
          <div className="captcha-package__info">
            <div className="captcha-package__info--phone">{spacePhoneNumber(mobile)}</div>
            <div className="captcha-package__info--desc">
              <span>{packageNameForPrice}</span> <span>{priceDesc}</span>
            </div>
          </div>
          <SMSCode codeLength={captchaLength} submit={completeInputCode} />
          <CountDown countdown={countdown} resendCode={deliverSMSCode} />
        </div>
        <div className="captcha-confirm-wrapper">
          {showConfirmBtn ? (
            <div
              className={cx('captcha-confirm-btn', {
                disabled: !captchaCode,
              })}
              onClick={submitConfirmCode}
            >
              <img
                className="captcha-confirm-btn-img"
                data-test-id="submit-sms-code-btn"
                src={isPromotional ? smsModalMobilePromotionImg : smsModalDefaultButtonImg}
              />
            </div>
          ) : null}
          {agreementConfig && <AgreementCheckbox ref={agreementRef} config={agreementConfig} packageId={packageId} />}
          {!showAgreement && <div className="captcha-confirm-tips">{orderTips}</div>}
        </div>
      </div>
    </Dialog>
  );
};

export function captchaModalInit(params) {
  return <SMSCaptchaModal {...params} />;
}

// 导出组件本身，方便直接调用
export { SMSCaptchaModal };
